import { useRef, useCallback, useEffect } from "react";
import {
  useSensor,
  useSensors,
  TouchSensor,
  MouseSensor,
} from "@dnd-kit/core";

/**
 * Custom hook for managing @dnd-kit drag-and-drop for exercises
 * Handles scroll prevention, keyboard dismissal, sensor configuration,
 * and auto-scroll coordination with ExerciseDragContext
 * 
 * Supports cross-workout moves with protocol slot restrictions
 */
export const useExerciseDragKit = ({
  startAutoScroll,
  stopAutoScroll,
  updateDragPosition,
} = {}) => {
  // Track scroll prevention state
  const scrollPreventionRef = useRef(null);

  // Configure sensors for both touch and mouse
  // TouchSensor: delay allows scrolling before drag starts (mobile)
  // MouseSensor: immediate activation for better desktop UX
  // PointerSensor: fallback for devices that support pointer events
  const touchSensor = useSensor(TouchSensor, {
    activationConstraint: {
      delay: 200, // milliseconds to hold before drag starts (allows scrolling)
      tolerance: 8, // pixels of motion allowed during delay
    },
  });

  const mouseSensor = useSensor(MouseSensor, {
    activationConstraint: {
      distance: 8, // pixels to move before drag starts (prevents accidental drags)
    },
  });

  // Use both sensors - dnd-kit will automatically choose the right one
  const sensors = useSensors(touchSensor, mouseSensor);

  // Cleanup scroll prevention on unmount
  useEffect(() => {
    return () => {
      // Restore scrolling if component unmounts during drag
      if (scrollPreventionRef.current) {
        scrollPreventionRef.current.forEach(
          ({ element, originalTouchAction, originalOverflow }) => {
            if (element) {
              element.style.touchAction = originalTouchAction || "";
              element.style.overflow = originalOverflow || "";
            }
          }
        );
        scrollPreventionRef.current = null;
      }
    };
  }, []);

  /**
   * Dismiss keyboard when drag starts
   */
  const dismissKeyboard = useCallback(() => {
    if (typeof document !== "undefined") {
      const activeElement = document.activeElement;
      if (
        activeElement &&
        (activeElement.tagName === "INPUT" ||
          activeElement.tagName === "TEXTAREA" ||
          activeElement.contentEditable === "true")
      ) {
        activeElement.blur();
      }
    }
  }, []);

  /**
   * Prevent scrolling on scroll containers during drag (mobile)
   */
  const preventScrolling = useCallback(() => {
    if (typeof document === "undefined") return;

    // Initialize array if needed
    if (!scrollPreventionRef.current) {
      scrollPreventionRef.current = [];
    }

    // Find React Native Web ScrollView components
    const scrollViews = document.querySelectorAll(
      "[data-testid*='scroll'], [class*='ScrollView'], div[style*='overflow'], div[style*='Overflow'], [data-scroll-container]"
    );

    scrollViews.forEach((container) => {
      const htmlElement = container;
      if (htmlElement && htmlElement.style) {
        const originalTouchAction = htmlElement.style.touchAction;
        const originalOverflow = htmlElement.style.overflow;
        const originalOverflowY = htmlElement.style.overflowY;
        const originalOverflowX = htmlElement.style.overflowX;

        // Note: For exercises we DON'T prevent scrolling - we want auto-scroll to work
        // Only store references for cleanup

        scrollPreventionRef.current.push({
          element: htmlElement,
          originalTouchAction,
          originalOverflow,
          originalOverflowY,
          originalOverflowX,
        });
      }
    });
  }, []);

  /**
   * Restore scrolling after drag ends
   */
  const restoreScrolling = useCallback(() => {
    if (scrollPreventionRef.current) {
      scrollPreventionRef.current.forEach(
        ({
          element,
          originalTouchAction,
          originalOverflow,
          originalOverflowY,
          originalOverflowX,
        }) => {
          if (element && element.style) {
            element.style.touchAction = originalTouchAction || "";
            element.style.overflow = originalOverflow || "";
            if (originalOverflowY !== undefined) {
              element.style.overflowY = originalOverflowY || "";
            }
            if (originalOverflowX !== undefined) {
              element.style.overflowX = originalOverflowX || "";
            }
          }
        }
      );
      scrollPreventionRef.current = null;
    }
  }, []);

  /**
   * Handler for drag start - dismisses keyboard and starts auto-scroll
   */
  const handleDragStart = useCallback(
    (event) => {
      // Dismiss keyboard
      dismissKeyboard();

      // Setup scroll tracking
      preventScrolling();

      // Start auto-scroll if available
      if (startAutoScroll) {
        startAutoScroll();
      }
    },
    [dismissKeyboard, preventScrolling, startAutoScroll]
  );

  /**
   * Handler for drag move.
   * Note: Position updates are handled by pointer tracking in ExerciseDragContext,
   * so this is mainly for potential future extensions.
   */
  const handleDragMove = useCallback(
    (event) => {
      // Position tracking is handled by global pointer listeners in ExerciseDragContext
      // This handler is kept for potential future use or extensions
    },
    []
  );

  /**
   * Handler for drag over - can be used for drop zone highlighting
   */
  const handleDragOver = useCallback((event) => {
    // Can be extended for custom drop zone logic
  }, []);

  /**
   * Handler for drag end - restores scrolling and clears state
   * Move logic should be handled separately in the component
   */
  const handleDragEnd = useCallback(
    (event) => {
      // Stop auto-scroll if available
      if (stopAutoScroll) {
        stopAutoScroll();
      }

      // Restore scrolling after a small delay to ensure DOM updates
      requestAnimationFrame(() => {
        restoreScrolling();
      });

      // Return event for parent to handle move logic
      return event;
    },
    [stopAutoScroll, restoreScrolling]
  );

  /**
   * Handler for drag cancel - cleanup without executing move
   */
  const handleDragCancel = useCallback(() => {
    // Stop auto-scroll
    if (stopAutoScroll) {
      stopAutoScroll();
    }

    // Restore scrolling
    requestAnimationFrame(() => {
      restoreScrolling();
    });
  }, [stopAutoScroll, restoreScrolling]);

  return {
    sensors,
    handleDragStart,
    handleDragMove,
    handleDragOver,
    handleDragEnd,
    handleDragCancel,
  };
};

export default useExerciseDragKit;
